import { useEffect, useRef } from "react";
import NoSleep from "nosleep.js";

export default function useWakeLock(enabled) {
  const wakeLockRef = useRef(null);
  const noSleepRef = useRef(null);
  const noSleepActivatedRef = useRef(false);
  const pointerHandlerRef = useRef(null);

  useEffect(() => {
    let cancelled = false;

    const setupNoSleep = () => {
      if (!enabled || noSleepActivatedRef.current) return;
      if (!noSleepRef.current) noSleepRef.current = new NoSleep();

      const activate = () => {
        if (!enabled) return;
        if (!noSleepActivatedRef.current) {
          try {
            noSleepRef.current.enable();
            noSleepActivatedRef.current = true;
          } catch (e) {}
        }
        if (pointerHandlerRef.current) {
          window.removeEventListener("pointerdown", pointerHandlerRef.current);
          window.removeEventListener("touchstart", pointerHandlerRef.current);
          pointerHandlerRef.current = null;
        }
      };

      // User gesture required on many browsers (esp. iOS)
      pointerHandlerRef.current = activate;
      window.addEventListener("pointerdown", activate, { once: true });
      window.addEventListener("touchstart", activate, { once: true });
    };

    const requestWakeLock = async () => {
      if (!enabled) return;

      if ("wakeLock" in navigator) {
        try {
          const lock = await navigator.wakeLock.request("screen");
          if (cancelled) {
            try {
              await lock.release();
            } catch {}
            return;
          }
          wakeLockRef.current = lock;

          lock.addEventListener("release", () => {
            wakeLockRef.current = null;
            if (enabled && document.visibilityState === "visible") {
              requestWakeLock();
            }
          });
        } catch (err) {
          setupNoSleep();
        }
      } else {
        setupNoSleep();
      }
    };

    requestWakeLock();

    const onVisibilityChange = () => {
      if (
        document.visibilityState === "visible" &&
        enabled &&
        "wakeLock" in navigator
      ) {
        requestWakeLock();
      }
    };
    document.addEventListener("visibilitychange", onVisibilityChange);

    return () => {
      cancelled = true;
      document.removeEventListener("visibilitychange", onVisibilityChange);

      if (pointerHandlerRef.current) {
        window.removeEventListener("pointerdown", pointerHandlerRef.current);
        window.removeEventListener("touchstart", pointerHandlerRef.current);
        pointerHandlerRef.current = null;
      }

      if (
        wakeLockRef.current &&
        typeof wakeLockRef.current.release === "function"
      ) {
        wakeLockRef.current.release().catch(() => {});
        wakeLockRef.current = null;
      }

      if (noSleepRef.current && noSleepActivatedRef.current) {
        try {
          noSleepRef.current.disable();
        } catch {}
        noSleepActivatedRef.current = false;
      }
    };
  }, [enabled]);
}
